/* eslint-disable no-param-reassign */
/* eslint-disable max-lines */
import { LAYOUT } from '../util/const';
import type { Token } from './token';
import { NormalView, type Spec as NormalViewSpec } from './MainNormalView';
import {
  LifecycleEvent,
  StandardChartView,
  type Spec as StandardChartViewSpec,
  mergeUpdateOption as mergeStandardChartViewUpdateOption
} from './MainStandardChartView';
import { HXKLineView, type Spec as HXKLineViewSpec } from './MainHXKLineView';
import { ExternalView, type Spec as ExternalViewSpec } from './MainExternalView';
import { SimpleKLineView, type Spec as SimpleKLineViewSpec } from './SecondarySimpleKLineView';
import { type ExtensionApi, buildExtensionApi } from './extension';
import { type Hook, presetHook } from '../hook';
import defaultsTokenBuilder from '../theme/defaultsToken';
import { initInsights } from '../insight/insightManager';
import { convertLegacySpec } from '../legacy/convertSpec';
import { convertInsightType } from '../legacy/convertAidp';
import { getLayer, loadScriptFromLayer } from '../layer';
import { generateUID } from '../util/common';

type TabularData = { [key: string]: string | number }[];

/**
 * 规范设计参考了 Vega-Lite 和 Plot
 *
 * @see https://vega.github.io/vega-lite/docs/spec.html
 * @see https://observablehq.com/plot/getting-started
 */
export interface Specification {
  /** Hook API */
  hook: Partial<Hook>;
  /** 预设 hook */
  presetHook: Partial<Hook>;
  /** 设计 tokens */
  token: Partial<Token>;
  /** 布局 */
  layout: (typeof LAYOUT)[keyof typeof LAYOUT];
  /** 数据集 */
  data: { metadata?: unknown; values: TabularData }[];
  /**
   * 视图配置，分块
   *
   * - 例如主副图视图中，主图为一个块，副图为另一个块
   */
  view: {
    /** 主图 */
    main: NormalViewSpec | StandardChartViewSpec | HXKLineViewSpec;
    /** 副图 */
    secondary?: SimpleKLineViewSpec;
  };
  /** StandardChart 主题 */
  theme?: string;
}

interface LegacySpecification extends Specification {
  legacy?: boolean;
  style?: Parameters<typeof mergeStandardChartViewUpdateOption>[1];
}

function buildBaseDomLayout(api: ExtensionApi, $wrapper: HTMLElement) {
  const uid = api.util.common.generateUID();
  const $style = document.createElement('style');
  const $container = document.createElement('div');
  const $mainView = document.createElement('section');
  const $secondaryView = document.createElement('section');
  let subKLineDomHeight = 168;
  if (api.layout === api.util.const.LAYOUT.SMALL) {
    subKLineDomHeight = 66;
  }
  if (api.layout === api.util.const.LAYOUT.SMALL_2_1) {
    subKLineDomHeight = 62;
  }

  $style.textContent = `
    .${uid} * {
      box-sizing: border-box;
    }
  `;

  $container.classList.add(uid);

  $container.style.cssText = `width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center;justify-content: center; background-color: ${api.token.backgroundColor};`;
  $mainView.style.cssText = 'width: 100%; flex: 1; height: 100%;';
  $secondaryView.style.cssText = `width: 100%; height: ${subKLineDomHeight}px; margin-top: 12px;`;

  $wrapper.appendChild($container);
  $container.appendChild($style);

  // debug id
  $container.setAttribute('__aigc-vis-id', 'view-container');
  $mainView.setAttribute('__aigc-vis-id', 'view-main');
  $secondaryView.setAttribute('__aigc-vis-id', 'view-secondary');

  return { $container, $mainView, $secondaryView };
}

function getBaseDomLayout(
  api: ExtensionApi,
  $dom: HTMLElement,
  layoutInfo: ReturnType<ExtensionApi['hook']['layout']>
) {
  if (!api.baseDom?.$container) {
    api.baseDom = buildBaseDomLayout(api, $dom);
    const { $container, $mainView, $secondaryView } = api.baseDom;
    $container.appendChild($mainView);
    if (layoutInfo.secondaryView.enable) {
      $container.appendChild($secondaryView);
    }
  }
  return api.baseDom;
}

export type CoreAPI = Pick<Specification, 'token' | 'layout' | 'data' | 'theme'> & {
  dom: HTMLElement;
  spec: Specification;
};

const storage = new Map<string, { api: ExtensionApi; destroy: () => void }>();
const DOM_ATTRIBUTE_KEY = '__aigc_vis_data_id';

export function destroy($dom: HTMLElement) {
  if (!$dom || typeof $dom === 'string') {
    throw new Error('`$dom` is required and should be an instance of a DOM element.');
  }

  const key = $dom.getAttribute(DOM_ATTRIBUTE_KEY)!;

  storage.get(key)?.destroy?.();
  storage.get(key)?.api?.cache?.clear?.();
  storage.delete(key);

  // eslint-disable-next-line no-param-reassign
  $dom.innerHTML = '';
}

function beforeUpdate(api: ExtensionApi) {
  const allEventNeedBeCleared: (keyof LifecycleEvent)[] = ['standardchart:play'];
  allEventNeedBeCleared.forEach(eventName => api.lifecycle?.off(eventName));
}

function defaultsSpec(spec: Specification): Specification {
  // eslint-disable-next-line prefer-const
  let { token, hook, layout = LAYOUT.LARGE } = spec;

  token = defaultsTokenBuilder(token);
  hook = typeof hook === 'undefined' ? presetHook : { ...presetHook, ...hook };

  return {
    ...spec,
    token,
    hook,
    presetHook,
    layout
  };
}

async function loadViewScripts(view: Specification['view']) {
  const loadPromises = view.main.layers.map(async layer => {
    const layerType = layer.type;
    const layerDefine = getLayer(layerType);
    await loadScriptFromLayer(layerDefine);
  });
  await Promise.all(loadPromises);
}

function validateLayout(layoutInfo: ReturnType<ExtensionApi['hook']['layout']>) {
  if (layoutInfo.mainView.hasStandardChartLayer && layoutInfo.mainView.hasHXKLineLayer) {
    throw new Error(
      "`DataVis` can't support `StandardChartLayer` and `HXKLineLayer` at the same time"
    );
  }
}

function preprocessAndRenderView(
  api: ExtensionApi,
  $view: HTMLElement,
  spec: Specification['view']['main'],
  ViewClass: typeof NormalView | typeof StandardChartView | typeof HXKLineView | typeof ExternalView
) {
  const viewBlock = api.views[ViewClass.name as keyof ExtensionApi['views']] || new ViewClass();
  viewBlock.preprocessing(api, spec);
  viewBlock.render(api, $view, spec);
  return viewBlock as InstanceType<typeof ViewClass>;
}

function setHXKlineChartToken(api: ExtensionApi) {
  if (HXKlineChart && api?.token?.dvHXKLine?.constants) {
    HXKlineChart.registerConstants('customTheme', api?.token?.dvHXKLine?.constants);
  }
}

function renderSecondaryView(
  api: ExtensionApi,
  $view: HTMLElement,
  spec: Specification['view']['secondary'],
  mainView: StandardChartView
) {
  const viewBlock = api.views.HXKLineView__secondary || new SimpleKLineView();
  viewBlock.render(api, $view, spec, mainView);
  return viewBlock;
}

function buildViews(
  api: ExtensionApi,
  spec: Specification,
  layoutInfo: ReturnType<ExtensionApi['hook']['layout']>,
  $mainView: HTMLElement,
  $secondaryView: HTMLElement
) {
  const views = {} as Partial<ExtensionApi['views']>;

  if (layoutInfo.mainView.hasNormalLayer) {
    views.NormalView = preprocessAndRenderView(
      api,
      $mainView,
      spec.view.main as NormalViewSpec,
      NormalView
    );
  }
  if (layoutInfo.mainView.hasStandardChartLayer) {
    views.StandardChartView = preprocessAndRenderView(
      api,
      $mainView,
      spec.view.main as StandardChartViewSpec,
      StandardChartView
    );
  }
  if (layoutInfo.mainView.hasHXKLineLayer) {
    setHXKlineChartToken(api);
    views.HXKLineView__main = preprocessAndRenderView(
      api,
      $mainView,
      spec.view.main as HXKLineViewSpec,
      HXKLineView
    );
  }
  if (layoutInfo.mainView.hasExternalLayer) {
    views.ExternalView = preprocessAndRenderView(
      api,
      $mainView,
      spec.view.main as ExternalViewSpec,
      ExternalView
    );
  }
  // secondary view
  if (
    layoutInfo.secondaryView.enable &&
    layoutInfo.secondaryView.hasSimpleKLine &&
    layoutInfo.mainView.hasStandardChartLayer
  ) {
    api.util.hxKLine.validateHXKlineComponents();
    setHXKlineChartToken(api);
    views.HXKLineView__secondary = renderSecondaryView(
      api,
      $secondaryView,
      spec.view.secondary,
      views.StandardChartView as StandardChartView
    );
  }

  return views as ExtensionApi['views'];
}

function destroyViews(views: ExtensionApi['views']) {
  Object.keys(views).forEach(viewBlock =>
    views[viewBlock as keyof ExtensionApi['views']].destroy()
  );
}

function isLegacySpecWithStyle(spec: Specification) {
  return (
    (spec as LegacySpecification).legacy &&
    typeof (spec as LegacySpecification).style !== 'undefined'
  );
}

export async function render($dom: HTMLElement, spec: Specification, api?: ExtensionApi) {
  if (api == null) {
    destroy($dom);
  }

  const originSpec = spec as LegacySpecification;
  // eslint-disable-next-line no-param-reassign, @typescript-eslint/no-use-before-define
  spec = convertLegacySpec(spec);
  // eslint-disable-next-line no-param-reassign
  spec = defaultsSpec(spec);

  // * bind
  const { hook, token, layout, data, view, theme } = spec;
  const visId = generateUID();

  // 构建全局api
  if (!api) {
    $dom.setAttribute(DOM_ATTRIBUTE_KEY, visId);
    // eslint-disable-next-line no-param-reassign
    api = buildExtensionApi({
      coreAPI: { dom: $dom, spec, layout, token, data, theme },
      hook: {
        ...presetHook,
        ...hook
      },
      visId
    });
  }
  console.log('api', api);

  // 动态加载CDN资源
  await loadViewScripts(view);

  // 同一个dom只会渲染最新一次的图表
  if (api.visId !== $dom.getAttribute(DOM_ATTRIBUTE_KEY)) {
    return {};
  }

  // *
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  // 处理叙事可视化项目的配置转换
  convertInsightType(api);

  // 初始化洞察
  initInsights(api);

  // * pre-conditions
  const layoutInfo = api.hook.layout(api);
  validateLayout(layoutInfo);

  // * build base view dom layout
  const { $mainView, $secondaryView } = getBaseDomLayout(api, $dom, layoutInfo);
  const views = buildViews(api, spec, layoutInfo, $mainView, $secondaryView);

  storage.set(visId, {
    api,
    destroy: () => destroyViews(views)
  });

  // HACK
  // TODO 判断依据有问题，首次渲染时若是用旧版数据格式并且带有style字段的话就会渲染两次图表
  if (isLegacySpecWithStyle(originSpec) && originSpec.style) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    await update($dom, {
      view: {
        main: originSpec.style
      }
    });
  }

  api.views = views;

  return views;
}

type UpdateParams = {
  view: {
    main: Parameters<typeof mergeStandardChartViewUpdateOption>[1];
  };
};

/**
 * 微调更新
 *
 * TODO
 */
export async function update($dom: HTMLElement, updateParams: UpdateParams) {
  if (!$dom || typeof $dom === 'string') {
    throw new Error('`$dom` is required and should be an instance of a DOM element.');
  }

  const key = $dom.getAttribute(DOM_ATTRIBUTE_KEY);
  if (!key) {
    console.warn('Instance does not exist on dom.');
    return;
  }

  const instance = storage.get(key)!;
  const { spec: oldSpec } = instance.api;
  const spec = { ...oldSpec };

  // ! 目前仅处理 StandardChartViewSpec
  spec.view.main = mergeStandardChartViewUpdateOption(
    spec.view.main as StandardChartViewSpec,
    updateParams.view.main || {}
  );

  // 如果是增量/微调/新增insight式的渲染，则消除重复状态
  // TODO 各组件在更新前需要清理的缓存应当下放到组件内部实现
  beforeUpdate(instance.api);
  await render($dom, spec, instance.api);
}
